import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import useVideoJSAds from 'hooks/useVideoJSAds';
import useCamAdVastTag from 'hooks/useCamAdVastTag';
import useAdPlayback from 'components/CamAdPlayer/useAdPlayback';
import { MidrollCTA } from 'components/CTAs';
import { Camera } from 'types/camera';
import { SpotDetails } from 'types/spot';
import config from 'config';

interface UseKbygRewindsPlayerAdsProps {
  camera: Camera;
  spot: SpotDetails;
  hasAdFreeCamPermissions: boolean;
  hasAdSupportedCamPermissions: boolean;
  isCamPaywallRedesignEnabled: boolean;
  enabledFeatures: {
    ads: {
      video: boolean;
    };
  };
  multiCamViewActive: boolean;
  isNonPremiumPlaylist: boolean;
  pauseCurrentPlayer: () => void;
  playCurrentPlayer: () => void;
  playerPlaying: boolean;
}

interface UseKbygRewindsPlayerAdsReturn {
  isVideoAdPluginsError: boolean;
  onVideoJsGoogleImaLoaded: () => void;
  onVideoJsGoogleImaError: (e: any) => void;
  videoAdPluginsLoaded: boolean;
  adVastUrl: string | undefined;
  adTagLoading: boolean;
  MidrollCTAComponent: React.FC;
  adSettings: {
    adVastTagUrl?: string;
    MidrollCTA: React.FC;
  };
  camPlayerAdConfigLoaded: boolean;
  adsRequired: boolean;
  camAdsReady: boolean;
  enableAdsFromLivePlayer: boolean;
  enableAdPlayer: boolean;
  setPlayAd: (playAd: () => void) => void;
  adRunning: boolean | undefined;
  setAdRunning: (running: boolean) => void;
  onPlayedDurationUpdate: (playedMs: number, resetPlayedMs: () => void) => void;
}

export const useKbygRewindsPlayerAds = ({
  camera,
  spot,
  hasAdFreeCamPermissions,
  hasAdSupportedCamPermissions,
  isCamPaywallRedesignEnabled,
  enabledFeatures,
  multiCamViewActive,
  isNonPremiumPlaylist,
  pauseCurrentPlayer,
  playCurrentPlayer,
  playerPlaying,
}: UseKbygRewindsPlayerAdsProps): UseKbygRewindsPlayerAdsReturn => {
  const { asPath } = useRouter();
  const [forceEnableCam, setForceEnableCam] = useState(false);

  const {
    isVideoAdPluginsError,
    onVideoJsGoogleImaLoaded,
    onVideoJsGoogleImaError,
    videoAdPluginsLoaded,
    setVideoJsGoogleImaPluginError,
  } = useVideoJSAds();

  useEffect(() => {
    if (
      forceEnableCam === true &&
      videoAdPluginsLoaded === false &&
      isVideoAdPluginsError === false
    ) {
      setVideoJsGoogleImaPluginError(true);
    }
  }, [forceEnableCam, isVideoAdPluginsError, setVideoJsGoogleImaPluginError, videoAdPluginsLoaded]);

  const { adVastUrl, adTagLoading } = useCamAdVastTag(hasAdFreeCamPermissions, camera?._id);

  // Force camera to load with canned pre-roll if an ad is not present within 5 seconds of mounting this component
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceEnableCam(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  const MidrollCTAComponent = useCallback<React.FC>(
    () => (
      <MidrollCTA
        segmentProperties={{
          location: 'mid roll',
          pageName: 'Spot Report',
          spotId: spot._id,
          spotName: spot.name,
        }}
        isEntitled={hasAdFreeCamPermissions}
        referrer={asPath}
        stillUrl={camera.stillUrl}
        spotName={spot.name}
        upgradeUrl={config.funnelUrl}
      />
    ),
    [asPath, camera.stillUrl, hasAdFreeCamPermissions, spot._id, spot.name],
  );

  const adSettings = useMemo(
    () => ({
      adVastTagUrl: adVastUrl || undefined,
      MidrollCTA: MidrollCTAComponent,
    }),
    [adVastUrl, MidrollCTAComponent],
  );

  const camPlayerAdConfigLoaded = useMemo(
    () => (!!adVastUrl && !adTagLoading) || forceEnableCam,
    [adVastUrl, adTagLoading, forceEnableCam],
  );

  const adsRequired = !hasAdFreeCamPermissions && enabledFeatures.ads.video === true;

  const camAdsReady =
    !adsRequired ||
    (camPlayerAdConfigLoaded && (videoAdPluginsLoaded || isVideoAdPluginsError || forceEnableCam));

  const enableAdsFromLivePlayer =
    adsRequired && !hasAdSupportedCamPermissions && !isCamPaywallRedesignEnabled;

  const enableAdPlayer = adsRequired && !enableAdsFromLivePlayer;

  const {
    setPlayAd,
    adRunning: adRunningState,
    setAdRunning,
    onPlayedDurationUpdate,
  } = useAdPlayback({
    cameraId: camera._id,
    multiCamViewActive,
    disableAds: !enableAdPlayer,
    hasAdSupportedCamPermissions,
    isNonPremiumPlaylist,
    pause: pauseCurrentPlayer,
    play: playCurrentPlayer,
    playerPlaying,
  });

  return {
    isVideoAdPluginsError,
    onVideoJsGoogleImaLoaded,
    onVideoJsGoogleImaError,
    videoAdPluginsLoaded,
    adVastUrl,
    adTagLoading,
    MidrollCTAComponent,
    adSettings,
    camPlayerAdConfigLoaded,
    adsRequired,
    camAdsReady,
    enableAdsFromLivePlayer,
    enableAdPlayer,
    setPlayAd,
    adRunning: adRunningState,
    setAdRunning,
    onPlayedDurationUpdate,
  };
};

export default useKbygRewindsPlayerAds;
